import { useCallback, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { useInvestorInvestorInvitationStart } from "@vega-api/generated/auth-invitation-investor-investor-v1/auth-invitation-investor-investor-v1";
import { useInvitationStart } from "@vega-api/generated/auth-invitation-v1/auth-invitation-v1";
import { type InvitationPermissionEntry } from "@vega-api/generated/types/invitationPermissionEntry";
import { type InvitationStartRequest } from "@vega-api/generated/types/invitationStartRequest";
import { type PermissionEntry } from "@vega-api/generated/types/permissionEntry";
import { type PortalInvitationStartRequest } from "@vega-api/generated/types/portalInvitationStartRequest";
import { type SubjectType } from "@vega-api/generated/types/subjectType";
import { getGetManageableUsersForLimitedPartnerQueryKey } from "@vega-api/generated/user-management-v1/user-management-v1";
import { SecondaryButton } from "@vega-core/components/Buttons/SecondaryButton";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";
import {
  OnboardingInviteType,
  trackInviteCreateAttempted,
  trackInviteCreateFailed,
} from "@vega-core/utils/posthog/onboardingAnalytics";

import { StepEmail } from "./StepEmail";
import { StepRoles } from "./StepRoles";

type Props = {
  open: boolean;
  onClose: VoidFunction;
};

type FormValues = {
  email: string;
  roles: Record<
    string,
    { roleId: string; subjectId: string; subjectType: SubjectType }
  >;
};

// Legacy (auth-invitation-v1). Delete with the legacy branch once
// gp_auth_invitation_v2 is fully rolled out.
const prepareInvitationStartRequest = (
  formValues: FormValues,
): InvitationStartRequest => {
  const { email, roles } = formValues;
  const entries: PermissionEntry[] = Object.entries(roles).map(
    ([subjectId, { roleId, subjectType }]) => ({
      roleId,
      subjectId,
      subjectType,
    }),
  );

  return {
    email,
    permissions: { entries },
  };
};

// New portal-pair API (investor-investor).
const prepareV2InvitationStartRequest = (
  formValues: FormValues,
): PortalInvitationStartRequest => {
  const { email, roles } = formValues;
  const entries: InvitationPermissionEntry[] = Object.entries(roles).map(
    ([subjectId, { roleId, subjectType }]) => ({
      roleId,
      subjectId,
      subjectType,
    }),
  );

  return {
    email,
    permissions: { entries },
  };
};

export const InviteUserModal = ({ onClose, open }: Props) => {
  const [activeStep, setActiveStep] = useState(0);
  const lastRoleCountRef = useRef(0);
  const queryClient = useQueryClient();

  const handleModalClose = () => {
    onClose();
    setActiveStep(0);
  };

  const steps = useMemo(
    () => [
      {
        title: m.userManagement_invite_title(),
        component: <StepEmail />,
        validationSchema: Yup.object({
          email: Yup.string()
            .email(m.formValidation_invalid({ name: m.formFields_email() }))
            .required(
              m.formValidation_required({
                name: m.formFields_email(),
              }),
            ),
        }),
      },
      {
        title: m.userManagement_invite_setRoles(),
        component: <StepRoles />,
        validationSchema: Yup.object().shape({
          roles: Yup.object()
            .test(
              "at-least-one-role",
              m.formValidation_fundAccessChoiceRequired(),
              (roles) => isTruthy(roles) && Object.keys(roles).length > 0,
            )
            .required(
              m.formValidation_required({
                name: m.formFields_role(),
              }),
            ),
        }),
      },
    ],
    [],
  );

  const isLastStep = activeStep === steps.length - 1;

  const showToast = useToast();
  const { featureFlag } = useFeatureFlag();

  const isInvitationV2 = featureFlag[FeatureFlag.GP_AUTH_INVITATION_V2];

  const invitationMutationOptions = {
    mutation: {
      onSuccess: () => {
        showToast({
          title: m.userManagement_invite_success(),
          variant: "success",
        });
        queryClient.invalidateQueries({
          queryKey: getGetManageableUsersForLimitedPartnerQueryKey(),
        });
        handleModalClose();
      },
      onError: (error: ErrorType<unknown>) => {
        trackInviteCreateFailed({
          inviteType: OnboardingInviteType.LP_USER,
          roleCount: lastRoleCountRef.current,
          error,
        });

        const { title, description } = getErrorMessage(error, {
          defaultTitle: m.userManagement_invite_error(),
        });
        showToast({
          title,
          description,
          variant: "destructive",
        });
      },
    },
  };

  const legacyInvitation = useInvitationStart(invitationMutationOptions);
  const investorInvestorInvitation = useInvestorInvestorInvitationStart(
    invitationMutationOptions,
  );

  const isPending = isInvitationV2
    ? investorInvestorInvitation.isPending
    : legacyInvitation.isPending;

  const handleNext = useCallback(
    (submitForm: () => void) => {
      if (!isLastStep) {
        setActiveStep((prev) => prev + 1);
      } else {
        submitForm();
      }
    },
    [isLastStep],
  );

  const handleClose = () => {
    handleModalClose();
  };

  const handleBack = () => {
    if (activeStep === 0) {
      handleModalClose();
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSubmit = useCallback(
    (values: FormValues) => {
      const roleCount = Object.keys(values.roles).length;

      lastRoleCountRef.current = roleCount;
      trackInviteCreateAttempted({
        inviteType: OnboardingInviteType.LP_USER,
        roleCount,
      });

      // --- New portal-pair API (investor-investor) ---
      if (isInvitationV2) {
        investorInvestorInvitation.mutate({
          data: prepareV2InvitationStartRequest(values),
        });

        return;
      }

      // --- Legacy (auth-invitation-v1). Delete this branch once
      // gp_auth_invitation_v2 is fully rolled out. ---
      legacyInvitation.mutate({ data: prepareInvitationStartRequest(values) });
    },
    [isInvitationV2, investorInvestorInvitation, legacyInvitation],
  );

  const { component, validationSchema, title } = steps[activeStep];

  return (
    <ActionaryModal
      data-testid="invite-user-dialog"
      fluidHeight
      open={open}
      setOpen={handleClose}
      title={title}
    >
      <Formik
        initialValues={{
          email: "",
          roles: {},
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isValid, submitForm, dirty }) => (
          <div className="flex flex-col gap-4">
            {component}
            <div className="flex w-full justify-end gap-4">
              <SecondaryButton
                dataTestId={activeStep === 0 ? "cancel-button" : "back-button"}
                fullWidth={false}
                onClick={handleBack}
              >
                {activeStep === 0 ? m.btn_cancel() : m.btn_back()}
              </SecondaryButton>
              <ThemedButton
                dataTestId={isLastStep ? "submit-button" : "next-button"}
                disabled={!isValid || !dirty}
                isLoading={isPending}
                onClick={() => handleNext(submitForm)}
                type="button"
              >
                {isLastStep ? m.btn_submit() : m.btn_next()}
              </ThemedButton>
            </div>
          </div>
        )}
      </Formik>
    </ActionaryModal>
  );
};
